import { StickerDef } from "../TrickStickerPicker/StickerDesigns";

// ─── Grid geometry ─────────────────────────────────────────────────────────
// These constants mirror the Tailwind classes on the sticker-sheet grid so the
// virtualizer's row maths match what CSS actually lays out:
//   px-4  → 16px horizontal padding each side
//   gap-2.5 → 10px gap between tiles
//   grid-template-columns: repeat(auto-fill, minmax(72px, 1fr))
export const SHEET_GRID_GAP = 10;
export const SHEET_GRID_PAD_X = 16;
export const SHEET_GRID_MIN_TILE = 72;

// How many equal columns fit a sheet `containerWidth` px wide — the JS twin of
// CSS `repeat(auto-fill, minmax(MIN_TILE, 1fr))`.
export function computeColumns(containerWidth: number): number {
    const inner = containerWidth - SHEET_GRID_PAD_X * 2;
    if (inner <= 0) return 1;
    return Math.max(1, Math.floor((inner + SHEET_GRID_GAP) / (SHEET_GRID_MIN_TILE + SHEET_GRID_GAP)));
}

// The square tile edge (which is also the row's content height, before the gap)
// for a given column count — tiles are `aspect-square` 1fr, so they stretch to
// fill the row.
export function computeTileSize(containerWidth: number, columns: number): number {
    const cols = Math.max(1, columns);
    const inner = containerWidth - SHEET_GRID_PAD_X * 2;
    const tile = (inner - (cols - 1) * SHEET_GRID_GAP) / cols;
    return Math.max(SHEET_GRID_MIN_TILE, Math.round(tile));
}

// Split a flat sticker list into rows of `columns`.
export function chunkIntoRows<T>(items: T[], columns: number): T[][] {
    const cols = Math.max(1, columns);
    const rows: T[][] = [];
    for (let i = 0; i < items.length; i += cols) rows.push(items.slice(i, i + cols));
    return rows;
}

// ─── Heterogeneous row model ───────────────────────────────────────────────
// The sheet body is ONE scroll surface virtualized as a flat list of rows of
// mixed kinds (section headers, grids of tiles, the pins skeleton, empty-state
// copy). This keeps every section windowed under a single virtualizer instead
// of mounting the whole ~140-tile General grid at once.

export type SheetRow =
    | { key: string; kind: "header"; title: string; color: string; count?: number }
    | { key: string; kind: "tiles"; defs: StickerDef[]; border: string; stagger: boolean }
    | { key: string; kind: "pinsLoading"; count: number }
    | { key: string; kind: "empty"; text: string };

export interface SheetSectionsInput {
    columns: number;
    hasPins: boolean;
    pinsLoading: boolean;
    pinTray: StickerDef[];
    pinSkeletonCount: number;
    // The already-filtered General set (search applied upstream).
    general: StickerDef[];
    tray: StickerDef[];
    query: string;
}

// Only the first couple of General rows carry the entry-pop stagger — they're
// the ones visible as the sheet springs up; scrolled-in rows mount plainly.
const STAGGER_ROWS = 2;

// Build the ordered row list the virtualizer renders. Pins + "For This Clip"
// are hidden while searching (search narrows General only), mirroring the
// pre-virtualization markup.
export function buildSheetRows(input: SheetSectionsInput): SheetRow[] {
    const { columns, hasPins, pinsLoading, pinTray, pinSkeletonCount, general, tray, query } = input;
    const hasQuery = query.trim().length > 0;
    const rows: SheetRow[] = [];

    // Only show the Pins section when there's actually something to show — a
    // loading skeleton or real pins. A rider with badge IDs that resolve to zero
    // stickers (all failed to load / no artwork) must NOT get an orphan header.
    if (hasPins && !hasQuery && (pinsLoading || pinTray.length > 0)) {
        rows.push({ key: "h:pins", kind: "header", title: "◆ Your Pins", color: "text-neon-pink" });
        if (pinsLoading) {
            rows.push({ key: "pins:loading", kind: "pinsLoading", count: Math.min(Math.max(pinSkeletonCount, 1), 4) });
        } else {
            chunkIntoRows(pinTray, columns).forEach((defs, i) =>
                rows.push({ key: `pins:${i}`, kind: "tiles", defs, border: "border-neon-pink/35", stagger: false }));
        }
    }

    rows.push({ key: "h:general", kind: "header", title: "✦ General", color: "text-electric-blue", count: general.length });
    if (general.length === 0) {
        rows.push({
            key: "general:empty",
            kind: "empty",
            text: hasQuery ? `No stickers match “${query}”.` : "No stickers yet.",
        });
    } else {
        chunkIntoRows(general, columns).forEach((defs, i) =>
            rows.push({ key: `general:${i}`, kind: "tiles", defs, border: "border-electric-blue/30", stagger: i < STAGGER_ROWS }));
    }

    if (!hasQuery) {
        rows.push({ key: "h:clip", kind: "header", title: "◎ For This Clip", color: "text-highlight-secondary" });
        if (tray.length === 0) {
            rows.push({
                key: "clip:empty",
                kind: "empty",
                text: "Tag your clip with a sport and feature to generate clip-specific stickers.",
            });
        } else {
            chunkIntoRows(tray, columns).forEach((defs, i) =>
                rows.push({ key: `clip:${i}`, kind: "tiles", defs, border: "border-highlight-secondary/30", stagger: false }));
        }
    }

    return rows;
}

// Estimated pixel height per row kind — close enough for the virtualizer to
// place rows before `measureElement` corrects them. Tile rows are the tile edge
// plus the inter-row gap.
export const SHEET_HEADER_ROW_EST = 44;
export const SHEET_EMPTY_ROW_EST = 60;

export function estimateRowSize(row: SheetRow | undefined, tileSize: number): number {
    if (!row) return tileSize + SHEET_GRID_GAP;
    switch (row.kind) {
        case "header":
            return SHEET_HEADER_ROW_EST;
        case "empty":
            return SHEET_EMPTY_ROW_EST;
        case "tiles":
        case "pinsLoading":
        default:
            return tileSize + SHEET_GRID_GAP;
    }
}
